import { useEffect, useState } from "react";

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { Dialog, DialogTitle } from "@mui/material";

import { useAppSelector } from "app/hooks";

import { selectCaseManagerConfiguration } from "features/config/store/selectors";

import { usePatchDossierInformationMutation } from "services/streamingApi/caseManager.api";

import { CreateDossierRequest } from "types/CreateDossierRequest";
import { Dossier } from "types/Dossier";
import {
    isPatchDossierInformationRequest,
    PatchDossierInformationRequest,
} from "types/PatchDossierInformationRequest";

import { DossierDialog } from "./DossierDialog";

export interface EditDossierDialogProps {
    closeDialog: () => void;
    dossier: Dossier;
}

export function EditDossierDialog(props: EditDossierDialogProps) {
    const caseManagerConfiguration = useAppSelector(
        selectCaseManagerConfiguration,
    );

    const [
        patchDossierInformation,
        { isLoading: isSaving, isSuccess, isError, error },
    ] = usePatchDossierInformationMutation();
    const [validationErrors, setValidationErrors] =
        useState<Record<string, string[]>>();
    const [genericError, setGenericError] = useState<object | undefined>(
        undefined,
    );

    useEffect(() => {
        if (isSuccess) {
            props.closeDialog();
        }
    }, [isSuccess]);

    useEffect(() => {
        setGenericError(undefined);
        setValidationErrors(undefined);

        if (!isError) return;

        const fetchBaseQueryError = error as FetchBaseQueryError;

        if (fetchBaseQueryError.status === 400 && fetchBaseQueryError.data) {
            const castError = fetchBaseQueryError.data as unknown as {
                errors: Record<string, string[]>;
            };
            setValidationErrors(castError.errors);
        } else {
            setGenericError(error);
        }
    }, [isError, error]);

    return (
        <Dialog
            open={true}
            fullWidth={true}
            maxWidth={"lg"}
            disableEscapeKeyDown
            onClose={(event: object, reason: string) => {
                if (reason !== "backdropClick") {
                    props.closeDialog();
                }
            }}
            slotProps={{
                paper: {
                    component: "form",
                    onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
                        event.preventDefault();

                        props.closeDialog();
                    },
                },
            }}
        >
            <DialogTitle>Edit dossier</DialogTitle>
            <DossierDialog
                onCancel={props.closeDialog}
                onSave={(
                    draftDossierRequest:
                        | CreateDossierRequest
                        | PatchDossierInformationRequest,
                ) =>
                    isPatchDossierInformationRequest(draftDossierRequest) &&
                    patchDossierInformation(draftDossierRequest)
                }
                isSaving={isSaving}
                existingDossier={props.dossier}
                genericError={genericError}
                validationErrors={validationErrors}
                availableDevices={props.dossier.dossierDevices}
                isLoadingAvailableDevices={false}
                errorLoadingAvailableDevices={undefined}
                maxNumberOfDays={caseManagerConfiguration?.maxNumberOfDays ?? 0}
            />
        </Dialog>
    );
}
